import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, BookOpen01Icon, Search01Icon, SparklesIcon } from '@hugeicons/core-free-icons';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '@/lib/api';
import { ArticleCard } from '@/components/kb/ArticleCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

export default function KBSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q')?.trim() || '';
  const [draft, setDraft] = useState(initialQuery);
  const [articles, setArticles] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setDraft(initialQuery);
    setLoadError(false);

    if (!initialQuery) {
      setArticles([]);
      return;
    }

    setArticles(null);
    api
      .get('/kb/articles', { params: { q: initialQuery } })
      .then(({ data }) => setArticles(data))
      .catch(() => {
        setArticles([]);
        setLoadError(true);
      });
  }, [initialQuery]);

  const resultLabel = useMemo(() => {
    if (!initialQuery) return 'Escribe una búsqueda para comenzar';
    if (articles === null) return 'Buscando recursos…';
    if (articles.length === 1) return '1 recurso encontrado';
    return `${articles.length} recursos encontrados`;
  }, [articles, initialQuery]);

  const submit = (event) => {
    event.preventDefault();
    const query = draft.trim();
    if (query) setSearchParams({ q: query });
  };

  return (
    <div className="min-h-[70vh]">
      <section className="relative overflow-hidden border-b border-border/50 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="absolute inset-0 via-grid opacity-40 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-6 py-14 md:py-20">
          <Link
            to="/kb"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4" aria-hidden="true" />
            Volver al Knowledge Hub
          </Link>

          <div className="flex items-center gap-2 mt-8 text-primary">
            <HugeiconsIcon icon={SparklesIcon} className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.16em]">Búsqueda inteligente</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold mt-3">Encuentra lo que necesitas</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Explora guías, políticas, prompts y buenas prácticas curadas para UC CHRISTUS.
          </p>

          <form onSubmit={submit} className="relative max-w-2xl mt-8">
            <HugeiconsIcon
              icon={Search01Icon}
              className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ej.: prompts clínicos, sesgo, privacidad…"
              className="h-14 rounded-2xl bg-card/90 pl-12 pr-28 text-base shadow-float backdrop-blur"
              aria-label="Buscar recursos"
              autoFocus
            />
            <Button type="submit" className="absolute right-2 top-2 h-10 rounded-xl px-5">
              Buscar
            </Button>
          </form>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-12" aria-live="polite">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Resultados</p>
            <h2 className="font-display text-xl font-bold mt-1">{resultLabel}</h2>
          </div>
          {initialQuery && <span className="text-sm text-muted-foreground truncate">“{initialQuery}”</span>}
        </div>

        {loadError ? (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-8 text-center" role="alert">
            <p className="font-semibold">No pudimos completar la búsqueda.</p>
            <p className="text-sm text-muted-foreground mt-1">Intenta nuevamente en unos segundos.</p>
          </div>
        ) : articles === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, index) => (
              <Skeleton key={index} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-border bg-card p-10 md:p-14 text-center shadow-card">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
              <HugeiconsIcon icon={BookOpen01Icon} className="h-6 w-6" aria-hidden="true" />
            </div>
            <h3 className="font-display text-xl font-bold mt-5">
              {initialQuery ? 'No encontramos coincidencias' : 'El conocimiento VIA está a un clic'}
            </h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              {initialQuery
                ? 'Prueba con un término más general o explora las categorías del Knowledge Hub.'
                : 'Busca por tema, herramienta, riesgo o tipo de recurso.'}
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-xl">
              <Link to="/kb">Explorar categorías</Link>
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
